import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, apiErrorMessage } from "@/lib/api";
import * as XLSX from "xlsx";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ClockCounterClockwise, CaretDown, CaretUp, MagnifyingGlass, ArrowsDownUp,
  CurrencyDollar, Calendar, ArrowsOutCardinal, List as ListIcon, Warning, ArrowsClockwise, FileXls,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { AuditPeriodBar, AuditDuplicates, AuditSubmissions, auditRange, longDate } from "@/components/timesheet/EntryAudit";
import RawDataPanel, { ViewPill } from "@/components/timesheet/raw/RawDataPanel";
import SourcePill from "@/components/timesheet/SourcePill";
import { ExportRawDataButton } from "@/components/timesheet/ExportRawDataButton";
import { InlineManualEntry } from "@/components/timesheet/InlineManualEntry";
import { ResizableBox } from "@/components/timesheet/ResizableBox";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Skeleton helpers — used while data is loading so each section shows a
// shimmer placeholder instead of an empty/“no data” message.
function TableSkeleton({ rows = 5, cols = 4 }) {
  return (
    <div className="w-full space-y-2 py-2" data-testid="section-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={`h-4 ${c === 0 ? "w-24" : "w-full"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

function fmtMoney(n) {
  if (!n && n !== 0) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDays(n) {
  return `${Number(n || 0).toFixed(2)} d`;
}
// Format a local-time Date as YYYY-MM-DD (avoids toISOString UTC shift bug)
function fmtLocalDate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Render a YYYY-MM-DD string with its 3-letter day-of-week suffix, e.g.
// "2026-06-15 Mon". Returns "—" for falsy / unparseable input.
const _DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function fmtDateWithDay(dateStr) {
  if (!dateStr) return "—";
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateStr;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return dateStr;
  return `${m[1]}-${m[2]}-${m[3]} ${_DAY_ABBR[d.getDay()]}`;
}

// Return the effective rate for (employee, kind in {cash|check}) on a given
// YYYY-MM-DD date string, given the salaries map (with cash_history/check_history).
// Falls back to the flat cash_amount/check_amount when no history rows exist.
function rateOn(salaries, employee, kind, dateStr) {
  if (!employee || !dateStr) return 0;
  const s = salaries[employee];
  if (!s) return 0;
  const hist = s[`${kind}_history`] || [];
  for (const r of hist) {
    const sd = r.start_date || "1970-01-01";
    const ed = r.end_date;
    if (sd <= dateStr && (!ed || ed >= dateStr)) return Number(r.amount) || 0;
  }
  if (hist.length === 0) return Number(s[`${kind}_amount`]) || 0;
  return 0;
}

function typeRateOn(salaries, employee, dateStr) {
  const s = salaries?.[employee];
  if (!s) return "";
  const hist = s.emp_type_history || [];
  if (!hist.length) return s.emp_type || "";
  const r = hist.find((h) => (h.start_date || "1970-01-01") <= dateStr && (!h.end_date || h.end_date >= dateStr));
  return r ? r.value || "" : "";
}

export default function TimesheetPage() {
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const isSuper = !!user?.is_super_admin;
  const [view, setView] = useState("dashboard");
  const [mgmt, setMgmt] = useState(null);
  useEffect(() => {
    if (perms.timesheet_management_access) api.get("/timesheet/mgmt/access").then(({ data }) => setMgmt(data)).catch(() => {});
  }, [perms.timesheet_management_access]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ raw_rows: [], entries: [], filters: { years: [], months: [], jobs: [], employees: [] }, fetched_at: null });
  const [error, setError] = useState("");

  // Meta-bar filters
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [job, setJob] = useState(null);
  const [employee, setEmployee] = useState(null);

  // Per-column searches
  const [colFilters, setColFilters] = useState({});
  const [sort, setSort] = useState({ key: "date_worked", dir: "desc" });

  // Section collapsed states — Timesheet table & AI default to collapsed
  const [kpiOpen, setKpiOpen] = useState(true);
  const [aiOpen] = useState(false); // retained to keep legacy storage key stable; unused
  const [tableOpen, setTableOpen] = useState(false);
  const [payrollOpen, setPayrollOpen] = useState(true);

  // Section order — saved per-user in localStorage so each user has their own preference.
  // AI Day Search was removed Feb 2026 (see "ai" migration below).
  const DEFAULT_ORDER = ["kpi", "payroll", "audit", "table"];
  const orderStorageKey = user?.id ? `ts:section-order:${user.id}` : null;
  const [sectionOrder, setSectionOrder] = useState(() => {
    if (!orderStorageKey) return DEFAULT_ORDER;
    try {
      const saved = JSON.parse(localStorage.getItem(orderStorageKey) || "null");
      if (Array.isArray(saved)) {
        // Drop the retired "ai" key from any older saved preference.
        const migrated = saved.filter((k) => k !== "ai");
        if (migrated.length === DEFAULT_ORDER.length && DEFAULT_ORDER.every((k) => migrated.includes(k))) {
          const a = migrated.indexOf("audit"), b = migrated.indexOf("payroll");
          if (a < b) { migrated[a] = "payroll"; migrated[b] = "audit"; }  // spec: Payroll always precedes Entry Audit
          return migrated;
        }
      }
    } catch {}
    return DEFAULT_ORDER;
  });
  const [reorderOpen, setReorderOpen] = useState(false);
  const persistOrder = (next) => {
    setSectionOrder(next);
    if (orderStorageKey) {
      try { localStorage.setItem(orderStorageKey, JSON.stringify(next)); } catch {}
    }
  };

  // "Add salaries" totals overlay
  const [showSalaryTotals, setShowSalaryTotals] = useState(false);

  // Custom Filter Mode — view-only, temporary, applied to the Days-per-Job
  // matrix and the Spend-by-Jobsite panel. Active state tracked here; the
  // modal uses pendingFilter* so Cancel never leaks half-selected state.
  const [customFilterOn, setCustomFilterOn] = useState(false);
  const [customFilterEmps, setCustomFilterEmps] = useState([]); // employee names
  const [customFilterJobs, setCustomFilterJobs] = useState([]); // job names
  const [customFilterOpen, setCustomFilterOpen] = useState(false);
  const [pendingFilterEmps, setPendingFilterEmps] = useState([]);
  const [pendingFilterJobs, setPendingFilterJobs] = useState([]);
  const [filterEmpSearch, setFilterEmpSearch] = useState("");
  const [filterJobSearch, setFilterJobSearch] = useState("");

  // AI (removed Feb 2026 — vars kept as no-ops in case of residual refs)
  // eslint-disable-next-line no-unused-vars
  const [aiQuestion, setAiQuestion] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [aiAnswer, setAiAnswer] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [aiBusy, setAiBusy] = useState(false);

  // Payroll
  const [payrollScope, setPayrollScope] = useState({ kind: "week", weeksAgo: 0 });
  const [payroll, setPayroll] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [salaries, setSalaries] = useState({}); // for showSalaryTotals math (super admin only)
  const [payrollCustomOpen, setPayrollCustomOpen] = useState(false);
  const payrollSeq = useRef(0);
  const [payrollCustomRange, setPayrollCustomRange] = useState({ start: "", end: "" });

  // Audit state (duplicates + missing entries)
  const [auditPeriod, setAuditPeriod] = useState({ kind: "recent" });
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const auditSeq = useRef(0);

  const loadTimesheet = async (force = false) => {
    // Only show the full-page loading skeleton on the *initial* fetch.
    // For auto-refreshes (force=false) and manual refresh clicks, keep the
    // existing data visible and only flip the small "refreshing" indicator.
    const isFirstLoad = !data?.entries;
    if (isFirstLoad) setLoading(true);
    else setRefreshing(true);
    setError("");
    try {
      const { data: resp } = await api.get(`/timesheet${force ? "?refresh=true" : ""}`);
      setData(resp);
      const years = resp?.filters?.years || [];
      if (years.length && !years.includes(year)) {
        setYear(years[0]); // default to most recent year (2026)
      }
    } catch (e) {
      const msg = apiErrorMessage(e);
      if (msg) setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadAudit = async (p) => {
    const seq = ++auditSeq.current;
    setAuditLoading(true);
    try {
      const r = auditRange(p.kind, p);
      const qs = r ? `?start_date=${r.start}&end_date=${r.end}` : "";
      const { data: resp } = await api.get(`/timesheet/audit${qs}`);
      if (seq === auditSeq.current) setAuditData(resp);
    } catch (e) {
      if (seq === auditSeq.current) toast.error(apiErrorMessage(e, "Entry audit failed to load"));
    } finally {
      if (seq === auditSeq.current) setAuditLoading(false);
    }
  };

  const loadPayroll = async (scope) => {
    if (!perms.payroll_access) return;
    setPayrollLoading(true);
    try {
      let qs;
      if (scope.kind === "week") {
        qs = `weeks_ago=${scope.weeksAgo}`;
      } else if (scope.kind === "month") {
        qs = `scope=month`;
      } else if (scope.kind === "custom") {
        if (!scope.start || !scope.end) { setPayrollLoading(false); return; }
        qs = `scope=custom&start_date=${scope.start}&end_date=${scope.end}`;
      }
      const seq = ++payrollSeq.current;
      const { data: resp } = await api.get(`/payroll?${qs}`);
      if (seq !== payrollSeq.current) return;  // a newer KPI range was requested; drop this stale response
      setPayroll(resp);
    } catch (e) {
      { const _m = apiErrorMessage(e); if (_m) toast.error(_m); }
    } finally {
      setPayrollLoading(false);
    }
  };

  // Load salaries for super admin (so "Add salaries/spend" toggle can compute).
  // Salaries are stored as WEEKLY amounts. CASH is a daily rate (divide by 5),
  // CHECK is now a flat weekly amount paid once per active week, regardless of
  // days worked — so it is NOT divided by 5.
  const loadSalaries = async () => {
    if (!isSuper) return;
    try {
      const { data: resp } = await api.get("/admin/salaries");
      const rows = Array.isArray(resp) ? resp : (resp?.rows || []);
      const mode = (resp && resp.mode) || "daily";
      const cashDiv = mode === "weekly" ? 5 : 1;
      const map = {};
      rows.forEach((r) => {
        const cash = (r.cash_amount || 0) / cashDiv;   // daily
        const check = (r.check_amount || 0);            // weekly (unchanged)
        map[r.employee] = {
          cash_amount: cash,
          check_amount: check,
          daily_rate: cash + check,
          add_pay_amount: r.add_pay_amount || 0,
          add_pay_history: r.add_pay_history || [],
          tax_wh_amount: r.tax_wh_amount || 0,
          tax_wh_history: r.tax_wh_history || [],
          emp_type: r.emp_type || "",
          emp_type_history: r.emp_type_history || [],
          cash_history: (r.cash_history || []).map((h) => ({ ...h, amount: (h.amount || 0) / cashDiv })),
          check_history: (r.check_history || []).map((h) => ({ ...h, amount: (h.amount || 0) })),
        };
      });
      setSalaries(map);
    } catch {
      // ignore (likely 403 if not super)
    }
  };

  useEffect(() => { loadTimesheet(); loadSalaries(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (payrollScope.kind === "custom") loadPayroll(payrollScope); /* eslint-disable-next-line */ }, [payrollScope, perms.payroll_access]);
  // Auto-refresh every 30s while the tab is visible — backend SWR cache makes
  // these calls return instantly from cache, and a background thread refreshes
  // the sheet so the next 30s tick has new data.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        loadTimesheet(false);
        loadAudit(auditPeriod);
      }
    }, 30000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [auditPeriod]);
  useEffect(() => { loadAudit(auditPeriod); /* eslint-disable-next-line */ }, [auditPeriod]);

  // Apply meta-bar filters to entries (tidy)
  const filteredEntries = useMemo(() => {
    return (data.entries || []).filter((e) => {
      if (year && e.year !== year) return false;
      if (month && e.month !== month) return false;
      if (job && e.job !== job) return false;
      if (employee && e.employee !== employee) return false;
      return true;
    });
  }, [data.entries, year, month, job, employee]);

  // Apply meta-bar filters to raw rows for the table view
  const filteredRows = useMemo(() => {
    const rows = (data.raw_rows || []).filter((r) => {
      if (year || month) {
        if (!r.date_worked) return false;
        const d = new Date(r.date_worked + "T00:00:00");
        if (year && d.getFullYear() !== year) return false;
        if (month && (d.getMonth() + 1) !== month) return false;
      }
      if (employee && r.name !== employee) return false;
      if (job) {
        if (![r.job_1, r.job_2, r.job_3, r.job_4].includes(job)) return false;
      }
      // Per-column search (substring, case-insensitive)
      for (const [k, q] of Object.entries(colFilters)) {
        const needle = (q || "").trim().toLowerCase();
        if (!needle) continue;
        const hay = String(r[k] ?? "").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    if (sort.key) {
      rows.sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (av === bv) return 0;
        const cmp = (av ?? "") > (bv ?? "") ? 1 : -1;
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return rows;
  }, [data.raw_rows, year, month, job, employee, colFilters, sort]);

  // KPI period toggle: "week" | "month" | "year" | "custom"
  const [kpiPeriod, setKpiPeriod] = useState("week");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [customOpen, setCustomOpen] = useState(false);

  // KPI: days per (employee × job) matrix + per-jobsite spend, scoped to selected period
  const kpiStats = useMemo(() => {
    const today = new Date();
    let start, end;
    if (kpiPeriod === "week") {
      const dow = today.getDay() || 7;
      start = new Date(today); start.setDate(today.getDate() - (dow - 1)); start.setHours(0, 0, 0, 0);
      end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    } else if (kpiPeriod === "last_week") {
      // Mon → Sun of the calendar week immediately before the current one.
      const dow = today.getDay() || 7;
      start = new Date(today); start.setDate(today.getDate() - (dow - 1) - 7); start.setHours(0, 0, 0, 0);
      end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    } else if (kpiPeriod === "two_weeks_ago") {
      // Mon → Sun of the full calendar week immediately before Last week (not a rolling 14 days).
      const dow = today.getDay() || 7;
      start = new Date(today); start.setDate(today.getDate() - (dow - 1) - 14); start.setHours(0, 0, 0, 0);
      end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    } else if (kpiPeriod === "month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
    } else if (kpiPeriod === "year") {
      start = new Date(today.getFullYear(), 0, 1, 0, 0, 0, 0);
      end = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
    } else {
      start = customRange.start ? new Date(customRange.start + "T00:00:00") : null;
      end = customRange.end ? new Date(customRange.end + "T23:59:59") : null;
    }

    const inRange = (data.entries || []).filter((e) => {
      if (!e.date) return false;
      const d = new Date(e.date + "T00:00:00");
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });

    // Matrix: rows = employees, cols = jobsites, cells = days + cash + check spend.
    // CASH is date-aware per-entry × days. CHECK is now a flat WEEKLY amount
    // paid once per (employee, ISO-week) regardless of days — so we allocate
    // it proportionally to each job's share of the employee's days that week.
    const empSet = new Set();
    const jobSet = new Set();
    const cell = {}; // key `${emp}|${job}` -> { days, cash, check }
    const empWeek = {}; // key `${emp}|${isoYear}-${isoWeek}` -> { emp, monday, totalDays, byJob:{job:days} }

    const isoWeekKeyFor = (jsDate) => {
      // ISO week-year + week number (Mon-Sun).
      const d = new Date(Date.UTC(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate()));
      const day = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - day);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
      return { key: `${d.getUTCFullYear()}-${String(weekNo).padStart(2, "0")}`, year: d.getUTCFullYear(), week: weekNo };
    };
    const mondayIsoOf = (jsDate) => {
      const dow = jsDate.getDay() || 7;
      const m = new Date(jsDate); m.setDate(jsDate.getDate() - (dow - 1));
      return m.toISOString().slice(0, 10);
    };

    inRange.forEach((e) => {
      if (!e.employee) return;
      empSet.add(e.employee);
      if (e.job) jobSet.add(e.job);
      const k = `${e.employee}|${e.job || ""}`;
      const c = cell[k] || (cell[k] = { days: 0, cash: 0, check: 0, add: 0, tax: 0 });
      const days = e.days || 0;
      c.days += days;
      // Daily cost model: every paid day costs Eff. Daily Rate = Weekly Total Gross ÷ 5 (rates on the WORK DATE),
      // split by component so Tax WH / Addit. Pmt are counted once per day, never per jobsite.
      c.cash += days * rateOn(salaries, e.employee, "cash", e.date);
      c.check += days * rateOn(salaries, e.employee, "check", e.date) / 5;
      c.add += days * rateOn(salaries, e.employee, "add_pay", e.date) / 5;
      c.tax += days * rateOn(salaries, e.employee, "tax_wh", e.date) / 5;
    });

    const jobs = Array.from(jobSet).sort();
    // Active roster (Admin > Salaries, excl. painters/archived) shows even with zero days; costs stay 0 (no cells).
    (data.roster || []).forEach((n) => empSet.add(n));
    const employees = Array.from(empSet).sort();
    const jobTotals = {};       // job -> days
    const jobCashTotals = {};   // job -> cash spend
    const jobCheckTotals = {};  // job -> check spend
    const jobAddTotals = {};    // job -> additional payment (weekly, allocated)
    const jobTaxTotals = {};    // job -> tax WH (weekly, allocated)
    const matrix = employees.map((emp) => {
      const row = { employee: emp, byJob: {}, total: 0, cash: 0, check: 0, add: 0, tax: 0 };
      jobs.forEach((j) => {
        const c = cell[`${emp}|${j}`] || { days: 0, cash: 0, check: 0, add: 0, tax: 0 };
        row.byJob[j] = c.days;
        row.total += c.days;
        row.cash += c.cash;
        row.check += c.check;
        row.add += c.add;
        row.tax += c.tax;
        jobAddTotals[j] = (jobAddTotals[j] || 0) + c.add;
        jobTaxTotals[j] = (jobTaxTotals[j] || 0) + c.tax;
        jobTotals[j] = (jobTotals[j] || 0) + c.days;
        jobCashTotals[j] = (jobCashTotals[j] || 0) + c.cash;
        jobCheckTotals[j] = (jobCheckTotals[j] || 0) + c.check;
      });
      return row;
    });
    matrix.sort((a, b) => b.total - a.total);

    // Per-job spend table — recompute from `cell` above so cash + check stay
    // in sync with the matrix.
    const byJob = {};
    jobs.forEach((j) => {
      byJob[j] = { job: j, days: jobTotals[j] || 0, cash: jobCashTotals[j] || 0, check: jobCheckTotals[j] || 0, add: jobAddTotals[j] || 0, tax: jobTaxTotals[j] || 0 };
    });
    const jobRows = Object.values(byJob)
      .map((r) => ({ ...r, spend: r.cash + r.check + r.add + r.tax }))
      .sort((a, b) => b.spend - a.spend);
    const totalDays = jobRows.reduce((s, r) => s + r.days, 0);
    const totalCash = jobRows.reduce((s, r) => s + r.cash, 0);
    const totalCheck = jobRows.reduce((s, r) => s + r.check, 0);
    const totalAdd = jobRows.reduce((s, r) => s + r.add, 0);
    const totalTax = jobRows.reduce((s, r) => s + r.tax, 0);
    const totalSpend = totalCash + totalCheck + totalAdd + totalTax;

    return {
      start: fmtLocalDate(start),
      end: fmtLocalDate(end),
      employees,
      jobs,
      matrix,
      cellMap: cell,
      jobTotals,
      jobCashTotals,
      jobCheckTotals,
      jobAddTotals,
      jobTaxTotals,
      jobRows,
      totalDays,
      totalCash,
      totalCheck,
      totalAdd,
      totalTax,
      totalSpend,
    };
  }, [data.entries, data.roster, salaries, kpiPeriod, customRange]);

  // Totals row of the table (filtered) — date-aware for cost.
  const totals = useMemo(() => {
    let totalDays = 0;
    let totalCost = 0;
    filteredRows.forEach((r) => {
      totalDays += r.total_days || 0;
      if (showSalaryTotals) {
        const d = r.date_worked || r.date_worked_display;
        const c = rateOn(salaries, r.name, "cash", d) + rateOn(salaries, r.name, "check", d);
        totalCost += (r.total_days || 0) * c;
      }
    });
    return { totalDays, totalCost };
  }, [filteredRows, showSalaryTotals, salaries]);

  const payrollTotals = useMemo(() => {
    const rows = payroll?.rows || [];
    return {
      days: rows.reduce((s, r) => s + (r.days || 0), 0),
      cash: rows.reduce((s, r) => s + (r.cash_total || 0), 0),
      check: rows.reduce((s, r) => s + (r.check_total || 0), 0),
      addPay: rows.reduce((s, r) => s + (r.additional_payment_total || 0), 0),
      pay: rows.reduce((s, r) => s + (r.total_pay || 0), 0),
      tax: rows.reduce((s, r) => s + (r.tax_wh_total || 0), 0),
      lessWh: rows.reduce((s, r) => s + (r.total_less_wh ?? r.total_pay ?? 0), 0),
    };
  }, [payroll]);

  /**
   * Filtered view of the KPI dataset. When Custom Filter Mode is ON, limits
   * to the user-selected employees + jobs and recomputes all totals from
   * only the visible intersections (never from hidden rows/cols). When OFF,
   * returns a straight passthrough of kpiStats so unaffected panels and the
   * export fast-paths continue to work exactly as before.
   */
  const kpiFiltered = useMemo(() => {
    if (!customFilterOn || !kpiStats.matrix?.length) {
      return { ...kpiStats, filtered: false };
    }
    const empSet = new Set(customFilterEmps);
    const jobSet = new Set(customFilterJobs);
    const jobs = kpiStats.jobs.filter((j) => jobSet.has(j));
    const jobSubsetSel = jobs.length < (kpiStats.jobs || []).length;
    // Jobsite-filtered: zero-day roster employees are dropped so no jobsite assignment is implied.
    const employees = kpiStats.employees.filter((e) => empSet.has(e) && (!jobSubsetSel || jobs.some((j) => (kpiStats.cellMap?.[`${e}|${j}`]?.days || 0) > 0)));
    const cellMap = kpiStats.cellMap || {};
    // Rebuild matrix with filtered byJob + recomputed row totals from visible cells only
    const matrix = employees.map((emp) => {
      const byJob = {};
      let total = 0, cash = 0, check = 0, add = 0, tax = 0;
      for (const j of jobs) {
        const k = `${emp}|${j}`;
        const c = cellMap[k];
        const d = c?.days || 0;
        byJob[j] = d;
        total += d;
        cash  += c?.cash  || 0;
        check += c?.check || 0;
        add   += c?.add   || 0;
        tax   += c?.tax   || 0;
      }
      return { employee: emp, byJob, total, cash, check, add, tax };
    });
    // Rebuild per-job and spend-by-jobsite from the visible intersections
    const jobTotals = {}, jobCashTotals = {}, jobCheckTotals = {}, jobAddTotals = {}, jobTaxTotals = {};
    jobs.forEach((j) => { jobTotals[j] = 0; jobCashTotals[j] = 0; jobCheckTotals[j] = 0; jobAddTotals[j] = 0; jobTaxTotals[j] = 0; });
    matrix.forEach((m) => {
      jobs.forEach((j) => {
        const k = `${m.employee}|${j}`;
        const c = cellMap[k];
        jobTotals[j]      += c?.days  || 0;
        jobCashTotals[j]  += c?.cash  || 0;
        jobCheckTotals[j] += c?.check || 0;
        jobAddTotals[j]   += c?.add   || 0;
        jobTaxTotals[j]   += c?.tax   || 0;
      });
    });
    const jobRows = jobs.map((j) => ({
      job: j, days: jobTotals[j], cash: jobCashTotals[j], check: jobCheckTotals[j], add: jobAddTotals[j], tax: jobTaxTotals[j],
      spend: (jobCashTotals[j] || 0) + (jobCheckTotals[j] || 0) + (jobAddTotals[j] || 0) + (jobTaxTotals[j] || 0),
    }));
    const totalDays  = matrix.reduce((s, m) => s + m.total, 0);
    const totalCash  = matrix.reduce((s, m) => s + m.cash,  0);
    const totalCheck = matrix.reduce((s, m) => s + m.check, 0);
    const totalAdd   = matrix.reduce((s, m) => s + m.add,   0);
    const totalTax   = matrix.reduce((s, m) => s + m.tax,   0);
    return {
      ...kpiStats, filtered: true, jobSubset: jobs.length < (kpiStats.jobs || []).length,
      employees, jobs, matrix, jobTotals, jobCashTotals, jobCheckTotals, jobAddTotals, jobTaxTotals,
      jobRows, totalDays, totalCash, totalCheck, totalAdd, totalTax, totalSpend: totalCash + totalCheck + totalAdd + totalTax,
    };
  }, [kpiStats, customFilterOn, customFilterEmps, customFilterJobs]);

  // Open the modal — seed pending state with the current active selection
  // (or all employees/jobs if filter is OFF so first-open is "everything selected")
  const openCustomFilter = () => {
    const seedEmps = customFilterOn ? customFilterEmps : (kpiStats.employees || []);
    const seedJobs = customFilterOn ? customFilterJobs : (kpiStats.jobs || []);
    setPendingFilterEmps([...seedEmps]);
    setPendingFilterJobs([...seedJobs]);
    setFilterEmpSearch(""); setFilterJobSearch("");
    setCustomFilterOpen(true);
  };
  const applyCustomFilter = () => {
    if (!pendingFilterEmps.length || !pendingFilterJobs.length) {
      toast.error("Pick at least one employee and one job to apply the filter.");
      return;
    }
    setCustomFilterEmps(pendingFilterEmps);
    setCustomFilterJobs(pendingFilterJobs);
    setCustomFilterOn(true);
    setCustomFilterOpen(false);
    toast.success(`Custom filter applied · ${pendingFilterEmps.length} employees × ${pendingFilterJobs.length} jobs`);
  };
  // When the global date range changes, prune any selections that no longer
  // exist in the new dataset so hidden rows can't quietly re-appear.
  useEffect(() => {
    if (!customFilterOn) return;
    const empSet = new Set(kpiStats.employees || []);
    const jobSet = new Set(kpiStats.jobs || []);
    const pEmps = customFilterEmps.filter((e) => empSet.has(e));
    const pJobs = customFilterJobs.filter((j) => jobSet.has(j));
    if (pEmps.length !== customFilterEmps.length) setCustomFilterEmps(pEmps);
    if (pJobs.length !== customFilterJobs.length) setCustomFilterJobs(pJobs);
    if (pEmps.length === 0 || pJobs.length === 0) setCustomFilterOn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiStats.employees?.join("|"), kpiStats.jobs?.join("|")]);

  /**
   * Export Detail to Excel — 3-sheet workbook (Employee Matrix, Job Labor
   * Detail, Job Cost Summary). Uses the kpiFiltered view so Custom Filter
   * Mode is reflected consistently across every sheet. Salary columns are
   * only emitted when the user has permission AND Add Salary Spend is ON
   * (defense in depth — ``isSuper`` is the server-recognized gate).
   *
   * Filename:
   *   OFF: STRATA_timesheet-all detail-YYYY-MM-DD-YYYY-MM-DD.xlsx
   *   ON : STRATA_timesheet-all detail-YYYY-MM-DD-YYYY-MM-DD_FILTERED.xlsx
   */
  useEffect(() => {
    if (kpiStats.start && kpiStats.end) setPayrollScope({ kind: "custom", start: kpiStats.start, end: kpiStats.end });
  }, [kpiStats.start, kpiStats.end]);

  const salRef = (emp, at) => {
    const d = at || kpiFiltered?.end;
    const cash = rateOn(salaries, emp, "cash", d) * 5, check = rateOn(salaries, emp, "check", d);
    const tax = rateOn(salaries, emp, "tax_wh", d), add = rateOn(salaries, emp, "add_pay", d);
    const gross = check + cash + tax + add;
    return { cash, check, tax, add, gross, daily: gross / 5, takeHome: gross - tax };
  };

  const exportLaborCost = () => {
    const view = kpiFiltered;
    const canSeeComp = !!(isSuper && showSalaryTotals);
    if (!view.employees?.length || !view.jobs?.length) {
      toast.info("No visible rows — pick employees and jobs that have activity in this period.");
      return;
    }
    const start = view.start;
    const end   = view.end;
    const money = '"$"#,##0.00;[Red]-"$"#,##0.00';
    const daysF = '#,##0.0';
    const wb = XLSX.utils.book_new();

    const typeOn = (emp) => typeRateOn(salaries, emp, end);
    // ---------- Sheet 1: Employee Matrix ----------
    const header1 = canSeeComp
      ? ["Employee", ...view.jobs, "Total Days", "Eff. Daily Rate", "Weekly Ttl Gross", "Tax WH", "Weekly Check", "Weekly Cash", "Addit. Pmt/Week", "Total Spend", "Type"]
      : ["Employee", ...view.jobs, "Total Days"];
    const rows1 = view.matrix.map((m) => {
      const dayCells = view.jobs.map((j) => m.byJob[j] || 0);
      if (!canSeeComp) return [m.employee, ...dayCells, m.total];
      const r = salRef(m.employee);
      return [m.employee, ...dayCells, m.total, r.daily, r.gross, r.tax, r.check, r.cash, r.add, m.cash + m.check + (m.add || 0) + (m.tax || 0), typeOn(m.employee)];
    });
    const jobColTotals = view.jobs.map((j) => view.jobTotals[j] || 0);
    const totalLaborCost = view.matrix.reduce((s, m) => s + m.cash + m.check + (m.add || 0) + (m.tax || 0), 0);
    const totalRow1 = canSeeComp
      ? ["TOTAL", ...jobColTotals, view.totalDays, "", "", "", "", "", "", totalLaborCost, ""]
      : ["TOTAL", ...jobColTotals, view.totalDays];
    const titleSuffix = view.filtered ? " · Custom Filter" : "";
    const aoa1 = [
      [`STRATA — Employee Labor Cost Matrix${titleSuffix}`],
      [`Reporting period: ${start} → ${end}`],
      [],
      header1, ...rows1, totalRow1,
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(aoa1);
    ws1["!cols"] = canSeeComp
      ? [{ wch: 22 }, ...view.jobs.map(() => ({ wch: 14 })), { wch: 11 }, { wch: 15 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 10 }]
      : [{ wch: 22 }, ...view.jobs.map(() => ({ wch: 14 })), { wch: 11 }];
    ws1["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 3 }, e: { c: header1.length - 1, r: 3 + rows1.length } }) };
    const applyFmt = (ws, startRow, endRow, cols, numFmt) => {
      for (let r = startRow; r <= endRow; r++) for (const c of cols) {
        const cell = ws[XLSX.utils.encode_cell({ c, r })];
        if (cell && typeof cell.v === "number") cell.z = numFmt;
      }
    };
    const dayCols = Array.from({ length: view.jobs.length + 1 }, (_, i) => 1 + i);
    applyFmt(ws1, 4, 4 + rows1.length, dayCols, daysF);
    if (canSeeComp) applyFmt(ws1, 4, 4 + rows1.length, [header1.length - 8, header1.length - 7, header1.length - 6, header1.length - 5, header1.length - 4, header1.length - 3, header1.length - 2], money);
    XLSX.utils.book_append_sheet(wb, ws1, "Employee Matrix");

    // ---------- Sheet 2: Job Labor Detail ----------
    const header2 = canSeeComp
      ? ["Employee", "Job Name", "Days Worked", "Eff. Daily Rate", "Tax WH", "Check", "Cash", "Addit. Pmt", "Total Spend", "Type"]
      : ["Employee", "Job Name", "Days Worked"];
    const detailRows = [];
    view.employees.forEach((emp) => {
      view.jobs.forEach((job) => {
        const k = `${emp}|${job}`;
        const c = view.cellMap?.[k];
        if (!c || !c.days) return;
        if (!canSeeComp) {
          detailRows.push([emp, job, c.days]);
        } else {
          const tot = c.cash + c.check + (c.add || 0) + (c.tax || 0);
          detailRows.push([emp, job, c.days, c.days > 0 ? tot / c.days : 0, c.tax || 0, c.check, c.cash, c.add || 0, tot, typeOn(emp)]);
        }
      });
    });
    const totalDetailDays = detailRows.reduce((s, r) => s + r[2], 0);
    const totalDetailCost = canSeeComp ? detailRows.reduce((s, r) => s + r[8], 0) : 0;
    const aoa2 = [
      [`STRATA — Job Labor Detail${titleSuffix}`],
      [`Reporting period: ${start} → ${end}`],
      [],
      header2, ...detailRows,
      canSeeComp ? ["TOTAL", "", totalDetailDays, "", ...[4, 5, 6, 7].map((i) => detailRows.reduce((s, r) => s + r[i], 0)), totalDetailCost, ""] : ["TOTAL", "", totalDetailDays],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(aoa2);
    ws2["!cols"] = canSeeComp
      ? [{ wch: 22 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 10 }]
      : [{ wch: 22 }, { wch: 24 }, { wch: 14 }];
    ws2["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 3 }, e: { c: header2.length - 1, r: 3 + detailRows.length } }) };
    applyFmt(ws2, 4, 4 + detailRows.length, [2], daysF);
    if (canSeeComp) applyFmt(ws2, 4, 4 + detailRows.length + 1, [3, 4, 5, 6, 7, 8], money);
    XLSX.utils.book_append_sheet(wb, ws2, "Job Labor Detail");

    // ---------- Sheet 3: Job Cost Summary ----------
    const header3 = canSeeComp
      ? ["Job Name", "Total Employee Days", "Total Tax WH", "Total Check", "Total Cash", "Total Addit. Pmt", "Total Spend"]
      : ["Job Name", "Total Employee Days"];
    const rows3 = view.jobRows.map((r) => canSeeComp
      ? [r.job, r.days, r.tax || 0, r.check, r.cash, r.add || 0, r.cash + r.check + (r.add || 0) + (r.tax || 0)]
      : [r.job, r.days]);
    const aoa3 = [
      [`STRATA — Job Cost Summary${titleSuffix}`],
      [`Reporting period: ${start} → ${end}`],
      [],
      header3, ...rows3,
      canSeeComp
        ? ["TOTAL", view.totalDays, view.totalTax || 0, view.totalCheck, view.totalCash, view.totalAdd || 0, view.totalSpend]
        : ["TOTAL", view.totalDays],
    ];
    const ws3 = XLSX.utils.aoa_to_sheet(aoa3);
    ws3["!cols"] = canSeeComp
      ? [{ wch: 24 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 20 }]
      : [{ wch: 24 }, { wch: 20 }];
    ws3["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 3 }, e: { c: header3.length - 1, r: 3 + rows3.length } }) };
    applyFmt(ws3, 4, 4 + rows3.length, [1], daysF);
    if (canSeeComp) applyFmt(ws3, 4, 4 + rows3.length + 1, [2, 3, 4, 5, 6], money);
    XLSX.utils.book_append_sheet(wb, ws3, "Job Cost Summary");

    // ---------- Sheet 4: Salary Detail (employee-level weekly reference rates, effective on the period end) ----------
    if (canSeeComp) {
      const wk = end;
      const [ey, em, ed] = end.split("-").map(Number);
      const endD = new Date(ey, em - 1, ed);
      endD.setDate(endD.getDate() - ((endD.getDay() + 6) % 7));
      const mon = fmtLocalDate(endD);
      const header4 = ["Employee", "Type", "Eff. Daily Rate", "Weekly Total Gross", "Weekly Take Home Pay", "Tax WH", "Weekly Check", "Weekly Cash", "Addit. Pmt/Week"];
      const rows4 = view.employees.map((emp) => {
        const cashW = rateOn(salaries, emp, "cash", wk) * 5;
        const checkW = rateOn(salaries, emp, "check", wk);
        const taxW = rateOn(salaries, emp, "tax_wh", wk);
        const addW = rateOn(salaries, emp, "add_pay", wk);
        const g = checkW + cashW + taxW + addW;
        return [emp, typeOn(emp), g / 5, g, g - taxW, taxW, checkW, cashW, addW];
      });
      const ws4 = XLSX.utils.aoa_to_sheet([
        [`STRATA — Salary Detail (weekly reference rates effective ${end})`],
        ["Reference rates only — do not add these to the allocated costs on the other sheets. Weekly Total Gross = Weekly Check + Weekly Cash + Tax WH + Addit. Pmt/Week; Eff. Daily Rate = Weekly Total Gross ÷ 5; Weekly Take Home Pay excludes Tax WH."],
        [],
        header4, ...rows4,
      ]);
      ws4["!cols"] = [{ wch: 22 }, { wch: 10 }, { wch: 14 }, { wch: 18 }, { wch: 20 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 16 }];
      applyFmt(ws4, 4, 4 + rows4.length, [2, 3, 4, 5, 6, 7, 8], money);
      XLSX.utils.book_append_sheet(wb, ws4, "Salary Detail");
    }

    const suffix = view.filtered ? "_FILTERED" : "";
    const filename = `STRATA_timesheet-all detail-${start}-${end}${suffix}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success(`${view.filtered ? "Filtered detail" : "Detail"} exported · ${view.employees.length} employees × ${view.jobs.length} jobs`);
  };

  if (!perms.timesheet_access) {
    return (
      <div className="min-h-screen bg-[#F4F6F8]">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-4 py-16">
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <h1 className="font-display text-xl font-semibold text-slate-900">Timesheet access required</h1>
            <p className="text-sm text-slate-500 mt-2">Ask the super admin to grant you Timesheet permission.</p>
          </div>
        </main>
      </div>
    );
  }

  const colHead = (label, key) => (
    <th key={key} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50 border-b border-slate-200">
      <button
        onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }))}
        className="flex items-center gap-1 hover:text-[#327de3]"
        data-testid={`col-sort-${key}`}
      >
        {label}
        <ArrowsDownUp size={11} className={sort.key === key ? "text-[#327de3]" : "text-slate-300"} />
      </button>
      <div className="mt-1.5">
        <div className="relative">
          <MagnifyingGlass size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-300" />
          <input
            placeholder="Filter…"
            value={colFilters[key] || ""}
            onChange={(e) => setColFilters((f) => ({ ...f, [key]: e.target.value }))}
            className="h-7 w-full pl-6 pr-2 text-[11px] rounded border border-slate-200 focus:border-[#327de3] focus:outline-none normal-case font-normal text-slate-700"
            data-testid={`col-filter-${key}`}
          />
        </div>
      </div>
    </th>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <AppHeader />
      <main className="max-w-7xl 2xl:max-w-none mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={22} weight="bold" className="text-[#327de3]" />
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Timesheet</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">Construction days by employee and jobsite.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {mgmt?.timesheet_management_access && (
              <ViewPill view={view} onChange={(v) => { setView(v); if (v === "dashboard") { loadTimesheet(false); loadAudit(auditPeriod); } }} />
            )}
            {view === "dashboard" && <>
            <SourcePill onChange={() => { loadTimesheet(false); loadAudit(auditPeriod); if (perms.payroll_access) loadPayroll(payrollScope); }} />
            {isSuper && (
              <label className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={showSalaryTotals}
                  onChange={(e) => setShowSalaryTotals(e.target.checked)}
                  className="h-4 w-4 accent-[#327de3]"
                  data-testid="toggle-add-salaries"
                />
                <CurrencyDollar size={15} weight="bold" className="text-emerald-600" />
                Add salaries/spend
              </label>
            )}
            <Button onClick={() => setReorderOpen(true)} variant="outline" size="sm" data-testid="ts-reorder-btn">
              <ArrowsOutCardinal size={14} weight="bold" className="mr-1.5" /> Reorder
            </Button>
            <div className="relative">
              <Button
                onClick={() => { loadTimesheet(true); loadAudit(auditPeriod); }}
                disabled={loading || refreshing}
                variant="outline"
                size="sm"
                data-testid="ts-refresh-btn"
                title="Re-fetch from Google Sheets now"
              >
                <ArrowsClockwise size={14} weight="bold" className={`mr-1.5 ${(loading || refreshing) ? "animate-spin" : ""}`} />
                {refreshing ? "Refreshing…" : (loading ? "Loading…" : "Refresh")}
              </Button>
              {data?.fetched_at && (
                <span
                  className="absolute right-0 top-full mt-0.5 whitespace-nowrap text-[10px] text-rose-300 tabular-nums"
                  data-testid="ts-last-refreshed"
                >
                  {refreshing ? "Refreshing…" : `Last refreshed ${new Date(data.fetched_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`}
                </span>
              )}
            </div>
            </>}
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 px-4 py-3" data-testid="ts-error">
            {error}
          </div>
        )}

        {/* Section wrapper — uses CSS flex order to honor user's reorder preference */}
        {view === "raw" && mgmt && <RawDataPanel access={mgmt} />}
        <div className={`flex flex-col ${view === "raw" ? "hidden" : "ts-xfade"}`} data-testid="ts-sections">
        {/* KPI SECTION — outlined cohesive card containing meta-bar + 2 KPI cards */}
        <div style={{ order: sectionOrder.indexOf("kpi") }}>
        <CollapsibleCard
          title="KPI"
          subtitle={`${fmtDateWithDay(kpiStats.start)} → ${fmtDateWithDay(kpiStats.end)}`}
          icon={<ClockCounterClockwise size={16} weight="bold" className="text-[#327de3]" />}
          open={kpiOpen}
          setOpen={setKpiOpen}
          testId="kpi-section-card"
        >
        {/* KPI META BAR — period selection for KPI cards */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 sm:p-4 mb-3 flex items-center justify-between flex-wrap gap-3" data-testid="kpi-meta-bar">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 mr-1">KPI period</div>
            {[
              { v: "week", label: "Current week" },
              { v: "last_week", label: "Last week" },
              { v: "two_weeks_ago", label: "2 Weeks Ago" },
              { v: "month", label: "Current month" },
              { v: "year", label: "Current year" },
              { v: "custom", label: "Custom date range" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => {
                  setKpiPeriod(opt.v);
                  if (opt.v === "custom") setCustomOpen(true);
                }}
                data-testid={`kpi-period-${opt.v}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  kpiPeriod === opt.v
                    ? "bg-[#327de3] text-white border-[#327de3]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#327de3] hover:text-[#1f64c2]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4 flex-wrap ml-auto">
            <div className="text-xs text-slate-500">
              <Calendar size={12} className="inline -mt-0.5 mr-1" />
              <span className="font-semibold text-slate-700">{fmtDateWithDay(kpiStats.start)}</span>
              <span className="mx-1">→</span>
              <span className="font-semibold text-slate-700">{fmtDateWithDay(kpiStats.end)}</span>
              {kpiPeriod === "custom" && (
                <button onClick={() => setCustomOpen(true)} className="ml-2 text-[#327de3] hover:underline" data-testid="kpi-period-custom-edit">Edit</button>
              )}
            </div>
            <div className="flex flex-col items-stretch gap-1.5 w-56" data-testid="kpi-export-stack">
              {isSuper && (
                <Button
                  size="sm"
                  onClick={exportLaborCost}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="kpi-export-labor-cost"
                  title="Download a 3-sheet workbook (employee matrix, job detail, job summary) using the current KPI period"
                >
                  <FileXls size={14} weight="bold" className="mr-1.5" /> Export All Detail To Excel
                </Button>
              )}
              <ExportRawDataButton start={kpiStats.start} end={kpiStats.end}
                employees={customFilterOn ? customFilterEmps : null} jobs={customFilterOn ? customFilterJobs : null} />
            </div>
          </div>
        </div>

        {/* KPI cards — full-width, stacked vertically */}
        <div className="flex flex-col gap-4 mb-5" data-testid="kpi-grid">
          <KpiCard
            title="Days Per Job Per Employee"
            subtitle={`${fmtDateWithDay(kpiFiltered.start)} → ${fmtDateWithDay(kpiFiltered.end)}${customFilterOn ? " · Custom Filter Mode Active" : ""}`}
            filtered={customFilterOn}
            action={
              <div className="flex items-center gap-2 flex-wrap">
                <CustomFilterToggle
                  on={customFilterOn}
                  empCount={kpiFiltered.filtered ? kpiFiltered.employees.length : customFilterEmps.length}
                  jobCount={customFilterJobs.length}
                  onToggle={() => {
                    if (customFilterOn) { setCustomFilterOn(false); toast.success("Custom filter cleared"); }
                    else openCustomFilter();
                  }}
                />
                {customFilterOn && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openCustomFilter(); }}
                    className="text-sm font-semibold text-orange-500 hover:text-orange-600 hover:underline underline-offset-2 transition-colors"
                    data-testid="matrix-edit-custom-filter"
                  >
                    Edit Filter
                  </button>
                )}
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  try {
                    const view = kpiFiltered;
                    const canSeeComp = !!(isSuper && showSalaryTotals);
                    if (!view?.matrix?.length || !view?.jobs?.length) {
                      toast.error("Nothing to export — no visible rows for the selected filter.");
                      return;
                    }
                    const isoStart = view.start;
                    const isoEnd = view.end;
                    const title = `STRATA TIMESHEET · Days per Job × Employee${view.filtered ? " · Custom Filter" : ""}`;
                    const header = canSeeComp
                      ? ["Employee", ...view.jobs, "Total", "Cash", "Check", "Addt'l Pay", "Total Spend"]
                      : ["Employee", ...view.jobs, "Total"];
                    const aoa = [
                      [title],
                      [`Period: ${isoStart} → ${isoEnd}`],
                      [""],
                      header,
                    ];
                    for (const row of view.matrix) {
                      const base = [
                        row.employee,
                        ...view.jobs.map((j) => ({ v: row.byJob[j] || 0, t: "n", z: "0.00" })),
                        { v: row.total || 0, t: "n", z: "0.00" },
                      ];
                      if (canSeeComp) {
                        base.push(
                          { v: row.cash || 0, t: "n", z: '"$"#,##0.00' },
                          { v: row.check || 0, t: "n", z: '"$"#,##0.00' },
                          { v: row.add || 0, t: "n", z: '"$"#,##0.00' },
                          { v: (row.cash || 0) + (row.check || 0) + (row.add || 0), t: "n", z: '"$"#,##0.00' },
                        );
                      }
                      aoa.push(base);
                    }
                    const totalRow = [
                      "TOTAL",
                      ...view.jobs.map((j) => ({ v: (view.jobTotals && view.jobTotals[j]) || 0, t: "n", z: "0.00" })),
                      { v: view.totalDays || 0, t: "n", z: "0.00" },
                    ];
                    if (canSeeComp) totalRow.push(
                      { v: view.totalCash || 0, t: "n", z: '"$"#,##0.00' },
                      { v: view.totalCheck || 0, t: "n", z: '"$"#,##0.00' },
                      { v: view.totalAdd || 0, t: "n", z: '"$"#,##0.00' },
                      { v: view.totalSpend || 0, t: "n", z: '"$"#,##0.00' },
                    );
                    aoa.push(totalRow);
                    const ws = XLSX.utils.aoa_to_sheet(aoa);
                    ws["!merges"] = [
                      { s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } },
                      { s: { r: 1, c: 0 }, e: { r: 1, c: header.length - 1 } },
                    ];
                    ws["!cols"] = canSeeComp
                      ? [{ wch: 22 }, ...view.jobs.map(() => ({ wch: 14 })), { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }]
                      : [{ wch: 22 }, ...view.jobs.map(() => ({ wch: 14 })), { wch: 10 }];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Days per Job x Employee");
                    const suffix = view.filtered ? "_FILTERED" : "";
                    const filename = `STRATA_timesheet-days per job only-${isoStart}-to-${isoEnd}${suffix}.xlsx`;
                    XLSX.writeFile(wb, filename);
                    toast.success(`${view.filtered ? "Filtered table" : "Table"} exported`);
                  } catch (err) {
                    console.error("Timesheet export failed:", err);
                    toast.error(`Export failed: ${err.message || err}`);
                  }
                }}
                data-testid="timesheet-export-matrix"
              >
                Export Table to Excel
              </Button>
              </div>
            }
          >
            {showSalaryTotals && (
              <div className="payroll-math-note mb-2 max-w-3xl rounded-md px-3 py-2 text-[11px] leading-snug" role="note" data-testid="kpi-salary-math-note">
                <p><b>Effective Daily Rate</b> = <b>Weekly Total Gross ÷ 5</b>. Weekly Total Gross = <b>Tax WH + Weekly Check + Weekly Cash + Addit. Pmt/Week</b> (salary version in effect on each work date).</p>
                <p className="mt-0.5">Weekly Total Gross covers a <b>5-day workweek</b>. Paid days beyond five are paid in <b>cash</b> at the Effective Daily Rate. Total Spend = paid days × Effective Daily Rate; Payroll pays the weekly amounts per week worked, so on short or partial weeks Payroll and Total Spend can differ.</p>
                <p className="mt-0.5">In Payroll, <b>vacation days</b> are flagged in yellow under <b>Days</b> (counted like a worked day) and <b>extra-day cash</b> is flagged in yellow under <b>Weekly Cash</b>.</p>
              </div>
            )}
            <ResizableBox testId="kpi-matrix-resize">
              {loading && !data.entries ? (
                <TableSkeleton rows={6} cols={5} />
              ) : kpiFiltered.matrix.length === 0 ? (
                <div className="text-xs text-slate-400 py-4 text-center">No days logged in the selected period.</div>
              ) : (
                <table className="w-full text-[11px] border-separate border-spacing-0" data-testid="kpi-matrix-table">
                  <thead className="z-20">
                    <tr>
                      <th className="sticky left-0 top-0 z-30 bg-white text-left py-1.5 pr-2 pl-2 text-[9px] uppercase tracking-widest text-slate-500 font-semibold border-b border-r border-slate-200">Employee</th>
                      {kpiFiltered.jobs.map((j) => (
                        <th key={j} className="sticky top-0 z-20 bg-white text-right py-1.5 px-1.5 text-[9px] uppercase tracking-widest text-slate-500 font-semibold whitespace-nowrap border-b border-slate-200" title={j}>
                          <div className="truncate max-w-[120px] text-right">{j}</div>
                        </th>
                      ))}
                      <th className="sticky top-0 z-20 bg-white text-right py-1.5 pl-2 pr-2 text-[9px] uppercase tracking-widest text-[#1f64c2] font-bold whitespace-nowrap border-b border-slate-200">Total</th>
                      {showSalaryTotals && (
                        <>
                          <th className="sticky top-0 z-20 bg-white text-right py-1.5 pl-2 pr-2 text-[9px] uppercase tracking-widest text-emerald-700 font-bold whitespace-nowrap border-b border-slate-200" data-testid="kpi-matrix-eff-daily-col" title="Effective Daily Rate = Weekly Total Gross ÷ 5">Eff. Daily Rate</th>
                          <th className="sticky top-0 z-20 bg-white text-right py-1.5 pl-2 pr-2 text-[9px] uppercase tracking-widest text-emerald-700 font-bold whitespace-nowrap border-b border-slate-200" data-testid="kpi-matrix-gross-col">Weekly Ttl Gross</th>
                          <th className="sticky top-0 z-20 bg-white text-right py-1.5 pl-2 pr-2 text-[9px] uppercase tracking-widest text-emerald-700 font-bold whitespace-nowrap border-b border-slate-200" data-testid="kpi-matrix-tax-wh-col">Tax WH</th>
                          <th className="sticky top-0 z-20 bg-white text-right py-1.5 pl-2 pr-2 text-[9px] uppercase tracking-widest text-emerald-700 font-bold whitespace-nowrap border-b border-slate-200">Weekly Check</th>
                          <th className="sticky top-0 z-20 bg-white text-right py-1.5 pl-2 pr-2 text-[9px] uppercase tracking-widest text-emerald-700 font-bold whitespace-nowrap border-b border-slate-200">Weekly Cash</th>
                          <th className="sticky top-0 z-20 bg-white text-right py-1.5 pl-2 pr-2 text-[9px] uppercase tracking-widest text-emerald-700 font-bold whitespace-nowrap border-b border-slate-200" data-testid="kpi-matrix-addl-pay-col">Addit. Pmt/Week</th>
                          <th className="sticky top-0 z-20 bg-white text-right py-1.5 pl-2 pr-2 text-[9px] uppercase tracking-widest text-emerald-800 font-extrabold whitespace-nowrap border-b border-slate-200">Total Spend</th>
                        </>
                      )}
                    </tr>
                    {/* Total Spend per-job sub-header (only when toggle is on) — broken down: Cash, Check, Total */}
                    {showSalaryTotals && (
                      <tr data-testid="kpi-matrix-total-spend-row">
                        <th className="sticky left-0 z-20 bg-emerald-50 text-left py-1.5 pr-2 pl-2 text-[11px] uppercase tracking-widest text-emerald-800 font-extrabold border-b border-r border-emerald-200">Total spend</th>
                        {kpiFiltered.jobs.map((j) => (
                          <th key={j} className="bg-emerald-50 text-right py-1.5 px-1.5 text-sm font-bold text-emerald-800 tabular-nums border-b border-emerald-200">
                            {fmtMoney((kpiFiltered.jobCashTotals[j] || 0) + (kpiFiltered.jobCheckTotals[j] || 0) + (kpiFiltered.jobAddTotals?.[j] || 0) + (kpiFiltered.jobTaxTotals?.[j] || 0))}
                          </th>
                        ))}
                        <th className="bg-emerald-50 border-b border-emerald-200" data-testid="kpi-matrix-total-days-blank" />
                        {kpiFiltered.jobSubset ? (
                          ["eff-daily", "gross", "tax", "check", "cash", "add"].map((k) => (
                            <th key={k} className="bg-emerald-50 border-b border-emerald-200" title="Blank: these rate references are company-wide, not specific to the selected jobsite(s)" data-testid={`kpi-matrix-sum-${k}-blank`} />
                          ))
                        ) : (() => {
                          const refs = kpiFiltered.employees.map((e) => salRef(e));
                          const sum = (k) => refs.reduce((t, r) => t + r[k], 0);
                          const tip = "Sum of each listed employee's weekly rate in effect at the period end, counted once per employee (a rate reference, not period cost)";
                          return (
                            <>
                              <th className="bg-emerald-50 text-right py-1.5 pl-2 pr-2 text-sm font-bold text-emerald-800 tabular-nums border-b border-emerald-200" title="Sum of employee effective daily rates (not total period cost)" data-testid="kpi-matrix-sum-eff-daily"><span className="eff-daily">{fmtMoney(sum("daily"))}</span></th>
                              <th className="bg-emerald-50 text-right py-1.5 pl-2 pr-2 text-sm font-bold text-emerald-800 tabular-nums border-b border-emerald-200" title={tip} data-testid="kpi-matrix-sum-gross">{fmtMoney(sum("gross"))}</th>
                              <th className="bg-emerald-50 text-right py-1.5 pl-2 pr-2 text-sm font-bold text-emerald-800 tabular-nums border-b border-emerald-200" title={tip} data-testid="kpi-matrix-sum-tax">{fmtMoney(sum("tax"))}</th>
                              <th className="bg-emerald-50 text-right py-1.5 pl-2 pr-2 text-sm font-bold text-emerald-800 tabular-nums border-b border-emerald-200" title={tip} data-testid="kpi-matrix-sum-check">{fmtMoney(sum("check"))}</th>
                              <th className="bg-emerald-50 text-right py-1.5 pl-2 pr-2 text-sm font-bold text-emerald-800 tabular-nums border-b border-emerald-200" title={tip} data-testid="kpi-matrix-sum-cash">{fmtMoney(sum("cash"))}</th>
                              <th className="bg-emerald-50 text-right py-1.5 pl-2 pr-2 text-sm font-bold text-emerald-800 tabular-nums border-b border-emerald-200" title={tip} data-testid="kpi-matrix-sum-add">{fmtMoney(sum("add"))}</th>
                            </>
                          );
                        })()}
                        <th className="bg-emerald-50 text-right py-1.5 pl-2 pr-2 text-sm font-extrabold text-emerald-800 tabular-nums border-b border-emerald-200" data-testid="kpi-matrix-grand-total">
                          {fmtMoney(kpiFiltered.totalSpend)}
                        </th>
                      </tr>
                    )}
                    {/* Job totals sub-header (days) */}
                    <tr>
                      <th className="sticky left-0 z-20 bg-slate-100 text-left py-1.5 pr-2 pl-2 text-[11px] uppercase tracking-widest text-slate-700 font-bold border-b border-r border-slate-200">Job total</th>
                      {kpiFiltered.jobs.map((j) => (
                        <th key={j} className="bg-slate-100 text-right py-1.5 px-1.5 text-sm font-bold text-slate-900 tabular-nums border-b border-slate-200">
                          {(kpiFiltered.jobTotals[j] || 0).toFixed(2)}d
                        </th>
                      ))}
                      <th className="bg-slate-100 text-right py-1.5 pl-2 pr-2 text-sm font-extrabold text-[#1f64c2] tabular-nums border-b border-slate-200">
                        {kpiFiltered.matrix.reduce((s, r) => s + r.total, 0).toFixed(2)}d
                      </th>
                      {showSalaryTotals && (
                        <>
                          <th className="bg-slate-100 border-b border-slate-200" />
                          <th className="bg-slate-100 border-b border-slate-200" />
                          <th className="bg-slate-100 border-b border-slate-200" />
                          <th className="bg-slate-100 border-b border-slate-200" />
                          <th className="bg-slate-100 border-b border-slate-200" />
                          <th className="bg-slate-100 border-b border-slate-200" />
                          <th className="bg-slate-100 border-b border-slate-200" />
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {kpiFiltered.matrix.map((row) => {
                      return (
                        <tr key={row.employee} className="hover:bg-slate-50/50 group">
                          <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50/50 py-1 pr-2 pl-2 font-medium text-slate-800 whitespace-nowrap border-b border-r border-slate-100 text-[11px]">{row.employee}{showSalaryTotals && typeRateOn(salaries, row.employee, kpiFiltered.end) && <span className="ml-1 px-1 rounded bg-slate-100 text-slate-500 text-[9px] font-semibold" data-testid={`kpi-matrix-type-${row.employee}`}>{typeRateOn(salaries, row.employee, kpiFiltered.end)}</span>}</td>
                          {kpiFiltered.jobs.map((j) => (
                            <td key={j} className="py-1 px-1.5 text-right tabular-nums text-slate-600 border-b border-slate-100 text-[11px]">
                              {row.byJob[j] ? row.byJob[j].toFixed(2) : "—"}
                            </td>
                          ))}
                          <td className="py-1 pl-2 pr-2 text-right tabular-nums font-bold text-[#1f64c2] border-b border-slate-100 text-[11px]">
                            {row.total.toFixed(2)}
                          </td>
                          {showSalaryTotals && (() => {
                            const r = salRef(row.employee);
                            const spend = row.cash + row.check + (row.add || 0) + (row.tax || 0);
                            const cls = "py-1 pl-2 pr-2 text-right tabular-nums font-medium text-emerald-700 border-b border-slate-100 text-[11px]";
                            return (
                              <>
                                <td className="py-1 pl-2 pr-2 text-right tabular-nums border-b border-slate-100 text-[11px]" data-testid={`kpi-matrix-eff-daily-${row.employee}`}><span className="eff-daily">{fmtMoney(r.daily)}</span></td>
                                <td className={cls} data-testid={`kpi-matrix-gross-${row.employee}`}>{fmtMoney(r.gross)}</td>
                                <td className={cls} data-testid={`kpi-matrix-tax-wh-${row.employee}`}>{fmtMoney(r.tax)}</td>
                                <td className={cls} data-testid={`kpi-matrix-check-${row.employee}`}>{fmtMoney(r.check)}</td>
                                <td className={cls} data-testid={`kpi-matrix-cash-${row.employee}`}>{fmtMoney(r.cash)}</td>
                                <td className={cls} data-testid={`kpi-matrix-addl-pay-${row.employee}`}>{fmtMoney(r.add)}</td>
                                <td className="py-1 pl-2 pr-2 text-right tabular-nums font-bold text-emerald-800 border-b border-slate-100 text-[11px]" data-testid={`kpi-matrix-total-spend-${row.employee}`}>{fmtMoney(spend)}</td>
                              </>
                            );
                          })()}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </ResizableBox>
          </KpiCard>
          <KpiCard
            title="Spend by Jobsite"
            subtitle={`${fmtDateWithDay(kpiFiltered.start)} → ${fmtDateWithDay(kpiFiltered.end)}${showSalaryTotals ? " · Days × daily rate (date-aware)" : " · Days only"}${customFilterOn ? " · Custom Filter Mode Active" : ""}`}
            accent="emerald"
            filtered={customFilterOn}
          >
            <div className="max-h-96 overflow-y-auto">
              {loading && !data.entries ? (
                <TableSkeleton rows={5} cols={3} />
              ) : kpiFiltered.jobRows.length === 0 ? (
                <div className="text-xs text-slate-400 py-4 text-center">No days logged in this period.</div>
              ) : (
                <table className="w-full text-[11px]" data-testid="kpi-spend-table">
                  <thead className="text-[9px] uppercase tracking-widest text-slate-500 sticky top-0 bg-white">
                    <tr>
                      <th className="text-left py-1.5 font-semibold">Jobsite</th>
                      <th className="text-right font-semibold">Days</th>
                      {showSalaryTotals && (
                        <>
                          <th className="text-right font-semibold text-emerald-700" data-testid="kpi-spend-tax-wh-col">Tax WH</th>
                          <th className="text-right font-semibold text-emerald-700">Check</th>
                          <th className="text-right font-semibold text-emerald-700">Cash</th>
                          <th className="text-right font-semibold text-emerald-700" data-testid="kpi-spend-addl-pay-col">Addit. Pmt</th>
                          <th className="text-right font-bold text-emerald-800">Total Spend</th>
                        </>
                      )}
                    </tr>
                    {/* TOTAL sub-header — larger/bolder */}
                    <tr className="bg-slate-100 tabular-nums">
                      <th className="text-left py-1.5 px-1 text-[10px] uppercase tracking-widest font-bold text-[#1f64c2]">Total</th>
                      <th className="text-right py-1.5 px-1 text-sm font-bold text-slate-900">{fmtDays(kpiFiltered.totalDays)}</th>
                      {showSalaryTotals && (
                        <>
                          <th className="text-right py-1.5 px-1 text-sm font-bold text-emerald-700" data-testid="kpi-spend-tax-wh-total">{fmtMoney(kpiFiltered.totalTax || 0)}</th>
                          <th className="text-right py-1.5 px-1 text-sm font-bold text-emerald-700">{fmtMoney(kpiFiltered.totalCheck)}</th>
                          <th className="text-right py-1.5 px-1 text-sm font-bold text-emerald-700">{fmtMoney(kpiFiltered.totalCash)}</th>
                          <th className="text-right py-1.5 px-1 text-sm font-bold text-emerald-700" data-testid="kpi-spend-addl-pay-total">{fmtMoney(kpiFiltered.totalAdd || 0)}</th>
                          <th className="text-right py-1.5 px-1 text-sm font-extrabold text-emerald-800" data-testid="kpi-spend-grand-total">{fmtMoney(kpiFiltered.totalSpend)}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {kpiFiltered.jobRows.map((r) => (
                      <tr key={r.job} className="border-t border-slate-100">
                        <td className="py-1 pr-2 text-slate-800 font-medium">{r.job}</td>
                        <td className="text-right text-slate-600 tabular-nums">{fmtDays(r.days)}</td>
                        {showSalaryTotals && (
                          <>
                            <td className="text-right font-medium text-emerald-700 tabular-nums">{fmtMoney(r.tax || 0)}</td>
                            <td className="text-right font-medium text-emerald-700 tabular-nums">{fmtMoney(r.check)}</td>
                            <td className="text-right font-medium text-emerald-700 tabular-nums">{fmtMoney(r.cash)}</td>
                            <td className="text-right font-medium text-emerald-700 tabular-nums">{fmtMoney(r.add || 0)}</td>
                            <td className="text-right font-bold text-emerald-800 tabular-nums" data-testid={`kpi-spend-total-${r.job}`}>{fmtMoney(r.spend)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </KpiCard>
        </div>
        </CollapsibleCard>
        </div>

        {/* AUDIT — duplicates + missing entries */}
        <div style={{ order: sectionOrder.indexOf("audit") }}>
          <CollapsibleCard
            title="Entry audit"
            subtitle={auditData ? (auditData.start_date === auditData.end_date ? longDate(auditData.start_date) : `${longDate(auditData.start_date)} → ${longDate(auditData.end_date)}`) : undefined}
            icon={<Warning size={16} weight="bold" className="text-amber-600" />}
            open={true}
            setOpen={() => {}}
            testId="audit-card"
            right={<AuditPeriodBar period={auditPeriod} onChange={setAuditPeriod} />}
          >
            <p className="text-xs text-slate-500 mb-3">
              Checks for <strong>duplicate</strong> entries (same employee logged the same date more than once),
              <strong> missing</strong> entries (employees who have worked in the last 30 days but didn&apos;t submit
              anything on each work day of the selected period), and <strong>inaccurate days</strong> for the week containing the period&apos;s last day
              (over-5-day totals + rows where the sheet&apos;s <em>Locations</em> column doesn&apos;t match the number
              of jobsite cells filled). Sundays are skipped automatically.
            </p>
            {auditLoading && !auditData && (
              <TableSkeleton rows={3} cols={2} />
            )}
            {auditData && (
              <div className={auditLoading ? "opacity-60 transition-opacity" : "transition-opacity"} data-testid="audit-body">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <AuditDuplicates data={auditData} />
                <AuditSubmissions data={auditData} />
              </div>
              <InaccurateDaysPanel data={auditData} />
              </div>
            )}
            {mgmt?.timesheet_raw_edit && (
              <InlineManualEntry onRefresh={async () => { await Promise.all([loadTimesheet(true), loadAudit(auditPeriod), perms.payroll_access ? loadPayroll(payrollScope) : null]); }} />
            )}
          </CollapsibleCard>
        </div>


        {/* PAYROLL — super admin / payroll_access only */}
        {perms.payroll_access && (
        <div style={{ order: sectionOrder.indexOf("payroll") }}>
          <CollapsibleCard
            title="Payroll"
            icon={<CurrencyDollar size={16} weight="bold" className="text-emerald-600" />}
            open={payrollOpen}
            setOpen={setPayrollOpen}
            testId="payroll-card"
            tone="emerald"
            right={<span className="text-xs font-semibold text-slate-600" data-testid="payroll-period-label">Payroll · {payroll?.week_start ? `${fmtDateWithDay(payroll.week_start)} → ${fmtDateWithDay(payroll.week_end)}` : "…"}</span>}
          >
            <div className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
              <Calendar size={12} />
              Follows the KPI period above ·
              <strong>{fmtDateWithDay(payroll?.week_start)}</strong> → <strong>{fmtDateWithDay(payroll?.week_end)}</strong>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payroll-table">
                <thead>
                  <tr className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="px-3 py-2 text-left">Employee</th>
                    <th className="px-3 py-2 text-right">Days</th>
                    {showSalaryTotals && <th className="px-3 py-2 text-right whitespace-nowrap" data-testid="payroll-eff-daily-col">Eff. Daily Rate</th>}
                    {showSalaryTotals && <th className="px-3 py-2 text-right whitespace-nowrap">Weekly Ttl Gross</th>}
                    {showSalaryTotals && <th className="px-3 py-2 text-right whitespace-nowrap" data-testid="payroll-tax-wh-col">Tax WH</th>}
                    {showSalaryTotals && <th className="px-3 py-2 text-right whitespace-nowrap">Weekly Check</th>}
                    {showSalaryTotals && <th className="px-3 py-2 text-right whitespace-nowrap">Weekly Cash</th>}
                    {showSalaryTotals && <th className="px-3 py-2 text-right whitespace-nowrap" data-testid="payroll-add-pay-col">Addit. Pmt/Week</th>}
                    {showSalaryTotals && <th className="px-3 py-2 text-right whitespace-nowrap">Total Spend</th>}
                    {showSalaryTotals && <th className="px-3 py-2 text-right whitespace-nowrap" data-testid="payroll-less-wh-col">Total (Less WH)</th>}
                  </tr>
                </thead>
                <tbody>
                  {payrollLoading && !payroll && (
                    <tr><td colSpan={showSalaryTotals ? 10 : 2} className="py-2">
                      <TableSkeleton rows={4} cols={showSalaryTotals ? 10 : 2} />
                    </td></tr>
                  )}
                  {!payrollLoading && (payroll?.rows || []).length === 0 && (
                    <tr><td colSpan={showSalaryTotals ? 10 : 2} className="text-center text-slate-400 py-4">No days for this week.</td></tr>
                  )}
                  {(payroll?.rows || []).map((r) => (
                    <tr key={r.employee} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-medium text-slate-900">{r.employee}</td>
                      <td className="px-3 py-2 text-right tabular-nums" data-testid={`payroll-days-${r.employee}`}>
                        {Number(r.days || 0).toFixed(2)}
                        {r.vacation_days > 0 && <div><span className="eff-daily payroll-pill" data-testid={`payroll-vacation-${r.employee}`}>Incl. ({+Number(r.vacation_days).toFixed(2)}d) Vacation</span></div>}
                      </td>
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums " data-testid={`payroll-eff-daily-${r.employee}`}><span className="eff-daily">{fmtMoney(salRef(r.employee, payroll?.week_end).daily)}</span></td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums text-emerald-700" title="Weekly Total Gross rate (reference) at the period end" data-testid={`payroll-gross-${r.employee}`}>{fmtMoney(salRef(r.employee, payroll?.week_end).gross)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums text-emerald-700" data-testid={`payroll-tax-wh-${r.employee}`}>{fmtMoney(r.tax_wh_total || 0)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{fmtMoney(r.check_total)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums text-emerald-700" data-testid={`payroll-cash-${r.employee}`}>{fmtMoney(r.cash_total)}{r.extra_days > 0 && <div><span className="eff-daily payroll-pill" data-testid={`payroll-extra-days-${r.employee}`}>Includes {+Number(r.extra_days).toFixed(2)} extra day{Number(r.extra_days) === 1 ? "" : "s"} at Eff. Daily Rate {fmtMoney(r.extra_day_cash / r.extra_days)} (over 5-day week)</span></div>}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums text-emerald-700" title={r.additional_note ? `Additional Payment: ${r.additional_note}` : "Additional Payment"} data-testid={`payroll-add-pay-${r.employee}`}>{fmtMoney(r.additional_payment_total)}{r.additional_note && <div className="text-[10px] text-slate-400">{r.additional_note}</div>}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-800" data-testid={`payroll-total-${r.employee}`}>{fmtMoney(r.total_pay)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-900" data-testid={`payroll-less-wh-${r.employee}`}>{fmtMoney(r.total_less_wh ?? r.total_pay)}</td>}
                    </tr>
                  ))}
                  {(payroll?.rows || []).length > 0 && (
                    <tr className="bg-slate-50 border-t-2 border-slate-300 font-semibold">
                      <td className="px-3 py-2 text-slate-900">Total</td>
                      <td className="px-3 py-2 text-right tabular-nums">{payrollTotals.days.toFixed(2)}</td>
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums"></td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums">{fmtMoney((payroll?.rows || []).reduce((t, r) => t + salRef(r.employee, payroll?.week_end).gross, 0))}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(payrollTotals.tax)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(payrollTotals.check)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(payrollTotals.cash)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(payrollTotals.addPay)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums" data-testid="payroll-grand-total">{fmtMoney(payrollTotals.pay)}</td>}
                      {showSalaryTotals && <td className="px-3 py-2 text-right tabular-nums" data-testid="payroll-grand-less-wh">{fmtMoney(payrollTotals.lessWh)}</td>}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleCard>
        </div>
        )}

        {/* TIMESHEET TABLE — collapsible */}
        <div style={{ order: sectionOrder.indexOf("table") }}>
        <CollapsibleCard
          title="Timesheet"
          subtitle={loading ? "Loading…" : `${filteredRows.length} of ${(data.raw_rows || []).length} rows`}
          icon={<ClockCounterClockwise size={16} weight="bold" className="text-[#327de3]" />}
          open={tableOpen}
          setOpen={setTableOpen}
          testId="timesheet-table-card"
        >
          {/* Filter bubbles INSIDE the table card so it's clear they scope the table */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-3 space-y-2.5" data-testid="meta-bar">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Filter table</div>
            <FlexRow label="Year" testId="filter-year" options={data.filters.years || []} value={year} onChange={setYear} formatter={(v) => v} />
            <FlexRow label="Month" testId="filter-month" options={data.filters.months || []} value={month} onChange={setMonth} formatter={(v) => MONTHS[v - 1] || v} />
            <FlexRow label="Job" testId="filter-job" options={data.filters.jobs || []} value={job} onChange={setJob} />
            <FlexRow label="Employee" testId="filter-employee" options={data.filters.employees || []} value={employee} onChange={setEmployee} />
          </div>

          {/* Totals strip */}
          <div className="bg-[#327de3]/5 border border-[#327de3]/15 rounded-md p-3 mb-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="table-totals">
            <TotalCell label="Rows" value={String(filteredRows.length)} />
            <TotalCell label="Total days" value={fmtDays(totals.totalDays)} />
            {showSalaryTotals && (
              <TotalCell label="Total spend ($)" value={fmtMoney(totals.totalCost)} valueClass="text-emerald-700" />
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="timesheet-table">
              <thead>
                <tr>
                  {colHead("Name", "name")}
                  {colHead("Date", "date_worked")}
                  {colHead("Locations", "locations")}
                  {colHead("1st Job", "job_1")}
                  {colHead("2nd Job", "job_2")}
                  {colHead("3rd Job", "job_3")}
                  {colHead("4th Job", "job_4")}
                  {colHead("Submitted At", "submitted_at")}
                </tr>
              </thead>
              <tbody>
                {loading && filteredRows.length === 0 && (
                  <tr><td colSpan={8} className="py-2">
                    <TableSkeleton rows={6} cols={8} />
                  </td></tr>
                )}
                {!loading && filteredRows.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-slate-400 py-6">No rows match the current filters.</td></tr>
                )}
                {filteredRows.map((r, i) => (
                  <tr key={`${r.name || ""}|${r.date_worked || r.date_worked_display || ""}|${r.submitted_at || i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
                    <td className="px-3 py-2 text-slate-700">{r.date_worked || r.date_worked_display}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.locations || ""}</td>
                    <td className="px-3 py-2 text-slate-700">{r.job_1 || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{r.job_2 || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{r.job_3 || "—"}</td>
                    <td className="px-3 py-2 text-slate-700">{r.job_4 || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.submitted_at_display || r.submitted_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleCard>
        </div>
        {/* end of flex-col sections wrapper */}
        </div>
      </main>

      {/* Reorder sections modal */}
      <ReorderDialog
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        order={sectionOrder}
        defaultOrder={DEFAULT_ORDER}
        onSave={(next) => { persistOrder(next); setReorderOpen(false); }}
      />

      {/* Payroll custom date range modal */}
      <Dialog open={payrollCustomOpen} onOpenChange={setPayrollCustomOpen}>
        <DialogContent className="sm:max-w-md" data-testid="payroll-custom-modal">
          <DialogHeader>
            <DialogTitle>Custom payroll date range</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label htmlFor="payroll-custom-start" className="text-xs text-slate-500">Start date</Label>
              <Input
                id="payroll-custom-start"
                type="date"
                value={payrollCustomRange.start}
                onChange={(e) => setPayrollCustomRange((r) => ({ ...r, start: e.target.value }))}
                data-testid="payroll-custom-start"
              />
            </div>
            <div>
              <Label htmlFor="payroll-custom-end" className="text-xs text-slate-500">End date</Label>
              <Input
                id="payroll-custom-end"
                type="date"
                value={payrollCustomRange.end}
                onChange={(e) => setPayrollCustomRange((r) => ({ ...r, end: e.target.value }))}
                data-testid="payroll-custom-end"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayrollCustomOpen(false)} data-testid="payroll-custom-cancel">Cancel</Button>
            <Button
              disabled={!payrollCustomRange.start || !payrollCustomRange.end}
              onClick={() => {
                setPayrollScope({ kind: "custom", start: payrollCustomRange.start, end: payrollCustomRange.end });
                setPayrollCustomOpen(false);
              }}
              className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
              data-testid="payroll-custom-apply"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom date range modal */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="sm:max-w-md" data-testid="kpi-custom-modal">
          <DialogHeader>
            <DialogTitle>Custom date range</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label htmlFor="kpi-custom-start" className="text-xs text-slate-500">Start date</Label>
              <Input
                id="kpi-custom-start"
                type="date"
                value={customRange.start}
                onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value }))}
                data-testid="kpi-custom-start"
              />
            </div>
            <div>
              <Label htmlFor="kpi-custom-end" className="text-xs text-slate-500">End date</Label>
              <Input
                id="kpi-custom-end"
                type="date"
                value={customRange.end}
                onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value }))}
                data-testid="kpi-custom-end"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setCustomRange({ start: "", end: "" }); }}
              data-testid="kpi-custom-clear"
            >
              Clear
            </Button>
            <Button
              onClick={() => setCustomOpen(false)}
              className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
              data-testid="kpi-custom-apply"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================
          CUSTOM FILTER MODE MODAL
          View-only, temporary filter on the Days-per-Job matrix + the
          Spend-by-Jobsite panel. Does NOT touch Payroll or any other
          Timesheet section.
          ============================================================ */}
      <Dialog open={customFilterOpen} onOpenChange={setCustomFilterOpen}>
        <DialogContent className="sm:max-w-3xl" data-testid="matrix-custom-filter-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListIcon size={16} className="text-orange-500" />
              Custom Filter — Days per Job per Employee + Spend by Jobsite
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-md border border-orange-200 bg-orange-50/60 px-3 py-2 text-[11px] text-orange-800">
            Pick any subset of employees and jobs for this date range. Totals in both panels recompute live. The filter is view-only — it doesn't change any underlying timesheet records.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            {[
              { label: "Employees", items: kpiStats.employees || [], picked: pendingFilterEmps, setPicked: setPendingFilterEmps, search: filterEmpSearch, setSearch: setFilterEmpSearch, testPrefix: "filter-emp" },
              { label: "Jobs / Job Sites", items: kpiStats.jobs || [], picked: pendingFilterJobs, setPicked: setPendingFilterJobs, search: filterJobSearch, setSearch: setFilterJobSearch, testPrefix: "filter-job" },
            ].map((col) => {
              const q = col.search.trim().toLowerCase();
              const visible = q ? col.items.filter((x) => x.toLowerCase().includes(q)) : col.items;
              const allPicked = visible.length > 0 && visible.every((v) => col.picked.includes(v));
              return (
                <div key={col.label} className="rounded-lg border border-slate-200 bg-white p-2 flex flex-col max-h-[400px]">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{col.label} <span className="text-slate-400">({col.picked.length}/{col.items.length})</span></div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => col.setPicked(Array.from(new Set([...col.picked, ...visible])))} className="text-[10px] font-semibold text-emerald-700 hover:underline" data-testid={`${col.testPrefix}-select-all`}>Select all</button>
                      <span className="text-slate-300">·</span>
                      <button type="button" onClick={() => col.setPicked(col.picked.filter((p) => !visible.includes(p)))} className="text-[10px] font-semibold text-rose-700 hover:underline" data-testid={`${col.testPrefix}-deselect-all`}>Deselect all</button>
                    </div>
                  </div>
                  <Input value={col.search} onChange={(e) => col.setSearch(e.target.value)} placeholder={`Search ${col.label.toLowerCase()}…`}
                    className="h-7 text-xs mb-2" data-testid={`${col.testPrefix}-search`} />
                  <div className="flex-1 overflow-y-auto space-y-0.5 border-t border-slate-100 pt-1">
                    {visible.length === 0 ? (
                      <div className="text-[11px] text-slate-400 italic text-center py-4">No matches.</div>
                    ) : visible.map((name) => (
                      <label key={name} className={`flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer ${col.picked.includes(name) ? "bg-orange-50" : "hover:bg-slate-50"}`}>
                        <input type="checkbox"
                          checked={col.picked.includes(name)}
                          onChange={() => col.setPicked((prev) => prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name])}
                          className="accent-orange-500"
                          data-testid={`${col.testPrefix}-chk-${name.replace(/[^a-zA-Z0-9]/g, "_")}`} />
                        <span className="truncate">{name}</span>
                      </label>
                    ))}
                  </div>
                  <div className="text-[9px] text-slate-400 mt-1 flex items-center justify-between">
                    <span>{allPicked ? "All visible selected" : `${visible.filter((v) => col.picked.includes(v)).length}/${visible.length} visible selected`}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCustomFilterOpen(false)} data-testid="matrix-custom-filter-cancel">Cancel</Button>
            <Button onClick={applyCustomFilter} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="matrix-custom-filter-apply">
              Apply filter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Helpers ---------- */
function InaccurateDaysPanel({ data }) {
  const over = data?.over_five_days || [];
  const mismatches = data?.location_mismatches || [];
  const weekStart = data?.week_start || "";
  const weekEnd = data?.week_end || "";
  const totalFlags = over.length + mismatches.length;
  return (
    <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50/40 p-3" data-testid="audit-inaccurate-days">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">
          Inaccurate days · week of{" "}
          <span className="text-rose-900 normal-case tracking-normal">{weekStart} → {weekEnd}</span>
        </div>
        <span
          className="text-xs font-bold text-rose-700 bg-white px-2 py-0.5 rounded-full"
          data-testid="audit-inaccurate-count"
        >
          {totalFlags}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Safety check for suspiciously inflated totals or copy-paste mistakes in the source Google
        Sheet. Rows below either exceed 5 days for the week or have a mismatch between the
        &quot;Locations&quot; column and the number of jobsite cells that were actually filled.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* OVER 5 DAYS */}
        <div className="rounded-md border border-rose-200 bg-white p-3" data-testid="audit-over-five">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">
              Over 5 days this week
            </div>
            <span
              className="text-xs font-bold text-rose-700"
              data-testid="audit-over-five-count"
            >
              {over.length}
            </span>
          </div>
          {over.length === 0 ? (
            <div className="text-xs text-slate-500 italic">
              No employees exceeded 5 days this week.
            </div>
          ) : (
            <ul className="text-xs text-slate-800 space-y-1.5 max-h-48 overflow-y-auto">
              {over.map((r, i) => (
                <li
                  key={`${r.employee}-${i}`}
                  className="flex items-center justify-between gap-2 bg-rose-50 rounded px-2 py-1.5 border border-rose-200"
                  data-testid={`audit-over-five-${r.employee}`}
                >
                  <span className="font-semibold text-rose-800">{r.employee}</span>
                  <span className="text-rose-700 font-bold tabular-nums">
                    {r.days} days
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="text-[10px] text-slate-400 mt-2">
            Recommendation: manually double-check these rows against the raw Google Sheet.
          </div>
        </div>
        {/* LOCATION MISMATCHES */}
        <div className="rounded-md border border-rose-200 bg-white p-3" data-testid="audit-loc-mismatch">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">
              Locations column mismatch
            </div>
            <span
              className="text-xs font-bold text-rose-700"
              data-testid="audit-loc-mismatch-count"
            >
              {mismatches.length}
            </span>
          </div>
          {mismatches.length === 0 ? (
            <div className="text-xs text-slate-500 italic">
              Every row&apos;s Locations column matches the number of jobsites filled.
            </div>
          ) : (
            <ul className="text-xs text-slate-800 space-y-1.5 max-h-56 overflow-y-auto">
              {mismatches.map((r, i) => (
                <li
                  key={`${r.employee}-${r.date}-${i}`}
                  className="bg-rose-50 rounded px-2 py-1.5 border border-rose-200"
                  data-testid={`audit-loc-mismatch-${r.employee}-${r.date}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-rose-800">{r.employee}</span>
                    <span className="text-slate-500">{r.date}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-600">
                    Sheet says{" "}
                    <strong className="text-rose-700">{r.locations_declared}</strong> location
                    {r.locations_declared === 1 ? "" : "s"} but{" "}
                    <strong className="text-rose-700">{r.jobsites_filled}</strong> jobsite
                    {r.jobsites_filled === 1 ? "" : "s"} filled ({r.jobs.join(", ")}).
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="text-[10px] text-slate-400 mt-2">
            Days are self-healed to use the higher of the two so the row never exceeds 1.0 day.
            Update the sheet&apos;s <em>Locations</em> column to clear the flag.
          </div>
        </div>
      </div>
    </div>
  );
}

function FlexRow({ label, options, value, onChange, formatter, testId }) {
  const fmt = formatter || ((v) => v);
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-slate-400 w-20 shrink-0">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        <FlexChip active={value === null} onClick={() => onChange(null)} testId={`${testId}-all`}>All</FlexChip>
        {options.map((opt) => (
          <FlexChip
            key={String(opt)}
            active={value === opt}
            onClick={() => onChange(opt)}
            testId={`${testId}-${opt}`}
          >
            {fmt(opt)}
          </FlexChip>
        ))}
      </div>
    </div>
  );
}

function FlexChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? "bg-[#327de3] text-white border-[#327de3]"
          : "bg-white text-slate-600 border-slate-200 hover:border-[#327de3] hover:text-[#1f64c2]"
      }`}
    >
      {children}
    </button>
  );
}

function CustomFilterToggle({ on, empCount, jobCount, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="inline-flex items-center gap-3 pl-4 pr-1.5 py-1.5 rounded-full border-2 border-orange-500 text-orange-500 hover:bg-orange-500/10 transition-colors"
      data-testid="matrix-custom-filter-toggle"
    >
      <span className="text-sm font-bold whitespace-nowrap">
        Custom Filter Mode
        {on && <span className="ml-1.5 font-semibold" data-testid="matrix-filter-active-counts">· {empCount} emp × {jobCount} jobs</span>}
      </span>
      <span className={`relative inline-block w-11 h-6 rounded-full transition-colors duration-200 ${on ? "cf-track-on" : "cf-track-off"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full cf-knob shadow transition-transform duration-200 ${on ? "translate-x-5" : "translate-x-0"}`} />
      </span>
      <span className="sr-only">{on ? "On" : "Off"}</span>
    </button>
  );
}

function KpiCard({ title, subtitle, children, accent, action, headerClassName = "", filtered = false }) {
  const stripe = filtered ? "bg-orange-500" : (accent === "emerald" ? "bg-emerald-500" : "bg-[#327de3]");
  const bodyTint = filtered ? "ring-2 ring-orange-500" : "";
  return (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${bodyTint}`}>
      <div className={`h-1.5 w-full ${stripe}`} />
      <div className="p-4">
        <div className={`mb-3 pb-3 border-b border-slate-100 flex items-start gap-3 ${headerClassName}`}>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg sm:text-xl font-bold text-slate-900 tracking-tight">{title}</div>
            {subtitle && <div className="text-sm text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          {action}
        </div>
        {children}
      </div>
    </div>
  );
}

function CollapsibleCard({ title, subtitle, icon, open, setOpen, children, testId, right, tone }) {
  // tone: "default" | "emerald"
  const bg = tone === "emerald" ? "bg-emerald-50/60" : "bg-white";
  const border = tone === "emerald" ? "border-emerald-200" : "border-slate-200";
  const headerBorder = tone === "emerald" ? "border-emerald-100" : "border-slate-100";
  return (
    <div className={`${bg} rounded-xl border ${border} shadow-sm mb-5 overflow-hidden`} data-testid={testId}>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${headerBorder}`}>
        <button
          onClick={() => setOpen((s) => !s)}
          className="flex items-center gap-2 text-left hover:text-[#327de3]"
          data-testid={`${testId}-toggle`}
        >
          {icon}
          <div>
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            {subtitle && <div className="text-[11px] text-slate-400">{subtitle}</div>}
          </div>
          {open ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </button>
        {right}
      </div>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

function TotalCell({ label, value, className = "", valueClass = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-sm font-bold text-slate-900 tabular-nums ${valueClass}`}>{value}</div>
    </div>
  );
}

/* ---------- Reorder Dialog (HTML5 drag-and-drop) ---------- */
const SECTION_LABELS = {
  kpi: { label: "KPI", desc: "KPI cards & period selector" },
  audit: { label: "Audit", desc: "Duplicate & missing-entry checks" },
  payroll: { label: "Payroll", desc: "Weekly payroll table" },
  table: { label: "Timesheet", desc: "Raw timesheet rows" },
};

function ReorderDialog({ open, onOpenChange, order, defaultOrder, onSave }) {
  const [local, setLocal] = useState(order);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  // Reset local whenever dialog opens
  useEffect(() => {
    if (open) {
      setLocal(order);
      setDragKey(null);
      setOverKey(null);
    }
  }, [open, order]);

  const handleDragStart = (key) => (e) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox
    try { e.dataTransfer.setData("text/plain", key); } catch {}
  };
  const handleDragOver = (key) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overKey !== key) setOverKey(key);
  };
  const handleDrop = (key) => (e) => {
    e.preventDefault();
    if (!dragKey || dragKey === key) {
      setDragKey(null); setOverKey(null); return;
    }
    const next = [...local];
    const from = next.indexOf(dragKey);
    const to = next.indexOf(key);
    if (from < 0 || to < 0) return;
    next.splice(from, 1);
    next.splice(to, 0, dragKey);
    setLocal(next);
    setDragKey(null);
    setOverKey(null);
  };
  const handleDragEnd = () => { setDragKey(null); setOverKey(null); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="ts-reorder-modal">
        <DialogHeader>
          <DialogTitle>Reorder sections</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1 mb-2">
          Drag and drop to reorder the sections on the Timesheet page. Your preference is saved on this device.
        </p>
        <ul className="space-y-2" data-testid="ts-reorder-list">
          {local.map((key) => {
            const meta = SECTION_LABELS[key] || { label: key, desc: "" };
            const isDragging = dragKey === key;
            const isOver = overKey === key && dragKey && dragKey !== key;
            return (
              <li
                key={key}
                draggable
                onDragStart={handleDragStart(key)}
                onDragOver={handleDragOver(key)}
                onDrop={handleDrop(key)}
                onDragEnd={handleDragEnd}
                data-testid={`ts-reorder-item-${key}`}
                className={`group flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5 cursor-grab active:cursor-grabbing select-none transition-all ${
                  isDragging ? "opacity-40 border-[#327de3]" : "border-slate-200 hover:border-[#327de3]/60"
                } ${isOver ? "ring-2 ring-[#327de3] ring-offset-1" : ""}`}
              >
                <ListIcon size={16} weight="bold" className="text-slate-400 group-hover:text-[#327de3]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{meta.desc}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-slate-400">Drag</span>
              </li>
            );
          })}
        </ul>
        <DialogFooter className="mt-3 sm:justify-between gap-2">
          <Button
            variant="outline"
            onClick={() => setLocal(defaultOrder)}
            data-testid="ts-reorder-restore"
          >
            Restore default
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="text-slate-600"
              data-testid="ts-reorder-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => onSave(local)}
              className="bg-[#327de3] hover:bg-[#1f64c2] text-white"
              data-testid="ts-reorder-save"
            >
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
